import React, { useCallback } from 'react';
import { ModalProps } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import { ClassNameMap } from '@mui/styles';
import makeStyles from '@mui/styles/makeStyles';
import { History } from 'history';
import { RouteProps, useHistory, useLocation } from 'react-router-dom';
import { useDrawerOnCloseContext } from './DrawerOnCloseContextProvider';
export const extractIdFromHash = (
  hash: string,
  path: string
): string | null => {
  const index = path.indexOf(':');
  const paramIndex = hash.indexOf('?');

  if (index <= 0) {
    return null;
  }

  return hash.startsWith(path.substring(0, index))
    ? paramIndex > index
      ? hash.substring(index, paramIndex)
      : hash.substring(index)
    : null;
};

export const doesHashMatchPath = (history: History, path: string): boolean => {
  if (!history || !history.location || !history.location.hash || !path) {
    return false;
  }

  return extractIdFromHash(history.location.hash, path) !== null;
};

export type TIDrawerRouteProps = RouteProps & {
  drawerWidth?: number;
  content: React.FC<{ value: string | null; onClose: () => void }>;
  classes?: ClassNameMap;
  modalProps?: Partial<ModalProps>;
};

const useDrawerStyles = makeStyles((theme) => ({
  paper: {
    [theme.breakpoints.down('md')]: {
      width: '100%'
    },
    width: ({ drawerWidth }: Partial<TIDrawerRouteProps>) => drawerWidth
  }
}));

export const DrawerRoute: React.FC<TIDrawerRouteProps> = ({
  path,
  content: DrawerContent,
  drawerWidth = 500,
  classes: injCls,
  modalProps
}: TIDrawerRouteProps) => {
  const classes = useDrawerStyles({ drawerWidth, classes: injCls });
  const history = useHistory();
  const location = useLocation();
  const routeValue = extractIdFromHash(location.hash, path as string);

  const onClose = useCallback(() => {
    const hasHashRoute = doesHashMatchPath(history, path as string);
    if (!hasHashRoute) return;

    history.replace(`${history.location.pathname}${location.search}`);
  }, [history, location, path]);
  const { drawerOnClose } = useDrawerOnCloseContext();

  const onBackdropClick = useCallback(() => {
    drawerOnClose?.();
  }, [drawerOnClose]);

  return (
    <Drawer
      classes={classes}
      variant="temporary"
      anchor="right"
      open={Boolean(routeValue)}
      onBackdropClick={onBackdropClick}
      ModalProps={modalProps}
      disableEnforceFocus
      data-testid="side-drawer"
    >
      {Boolean(routeValue) && (
        <>
          <DrawerContent
            value={routeValue}
            onClose={onClose}
            data-testid="drawer-content"
          />
        </>
      )}
    </Drawer>
  );
};
